import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { apiDelete, apiPatch, apiPost, operationsClient } from '@/lib/api';
import { timesheetEndpoints } from '@/lib/api/endpoints';
import { timesheetKeys } from '@/lib/queryKeys';
import type {
  OverrideCreateTimesheetEntryDto,
  OverrideUpdateTimesheetEntryDto,
  TimesheetDayEntry,
} from '@/types/timesheet';

/**
 * The owner/admin override valve, as three hooks.
 *
 * The normal edit/create/delete routes refuse once the job has been invoiced or
 * the pay period has been approved. The backend has always had a sanctioned way
 * past that, gated on `timesheets:approve` and recorded with a reason — but
 * nothing in this app ever called it, so an owner correcting a technician's
 * timesheet saw "an invoice has been issued" and had no way forward at all.
 * These are that way forward.
 *
 * Every call needs a reason. That is the whole bargain: the lock gives way, and
 * the audit trail records who moved it and why.
 */
export function useOverrideUpdateTimesheetEntry() {
  const queryClient = useQueryClient();
  return useApiMutation<
    TimesheetDayEntry,
    { id: string; dto: OverrideUpdateTimesheetEntryDto }
  >({
    mutationFn: ({ id, dto }) =>
      apiPatch<TimesheetDayEntry, OverrideUpdateTimesheetEntryDto>(
        operationsClient,
        timesheetEndpoints.overrideEntry(id),
        dto,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all() });
      toast.success('Locked entry corrected — recorded in the audit trail');
    },
  });
}

export function useOverrideDeleteTimesheetEntry() {
  const queryClient = useQueryClient();
  return useApiMutation<unknown, { id: string; reason: string }>({
    // DELETE with a body: the reason is mandatory, and axios only sends one on
    // a delete through `config.data`.
    mutationFn: ({ id, reason }) =>
      apiDelete(operationsClient, timesheetEndpoints.overrideEntry(id), {
        data: { reason },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all() });
      toast.success('Locked entry deleted — recorded in the audit trail');
    },
  });
}

/** Add a missing entry to already-invoiced or already-approved work. */
export function useOverrideCreateTimesheetEntry() {
  const queryClient = useQueryClient();
  return useApiMutation<
    TimesheetDayEntry,
    { appointmentId: string | null; dto: OverrideCreateTimesheetEntryDto }
  >({
    mutationFn: ({ appointmentId, dto }) =>
      apiPost<TimesheetDayEntry, OverrideCreateTimesheetEntryDto>(
        operationsClient,
        timesheetEndpoints.overrideCreate(),
        { ...dto, appointmentId: appointmentId ?? null },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all() });
      toast.success('Entry added to locked work — recorded in the audit trail');
    },
  });
}
